/**
 * An approval in plain words (web app card, Pulse summary): one sentence saying what the agent wants to do (« Envoyer
 * ce mail à lea@example.com », « Payer 64,20 € »), at most one preview (the words that will be sent, an amount, or a
 * warning), and a one-word outcome once decided. Built from the server-rendered view (title and lines, themselves
 * rendered from the exact stored action, never from the model's words); everything else stays in « Détails ».
 */
import type { ApprovalView } from "./contracts";

type View = Pick<ApprovalView, "title" | "lines" | "kind" | "status"> & { action?: { toolSlug: string }; ruleAction?: ApprovalView["ruleAction"] };

/** Lines whose value is words someone will read (a message, an answer): the quoted preview. */

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF" };

/** « 64,20 € » rather than « 64,20 EUR »: the symbol for common currencies, the code otherwise. */
function money(amount: string, currency: string | undefined | null): string {
  if (!currency) return amount;
  const code = currency.trim().toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code];
  return `${amount}\u00a0${symbol ?? currency.trim()}`;
}

export const QUOTED_LABELS = new Set(["Contenu", "Réponse", "Tes mots"]);

function line(view: View, ...labels: string[]): string | null {
  for (const label of labels) {
    const value = view.lines.find((l) => l.label === label)?.value?.trim();
    if (value) return value;
  }
  return null;
}

function short(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** "32 : a@x, b@y…" (renderAppAction's count prefix) → "32 personnes"; one value is kept as is. */
function who(value: string | null): string | null {
  if (!value) return null;
  const many = /^(\d+) : /.exec(value);
  if (many) return `${many[1]} personnes`;
  return short(value, 60);
}

function appOf(title: string): string | null {
  const m = /^(?:Lecture confidentielle · )?(.+?) · .+$/u.exec(title);
  return m ? m[1]! : null;
}

/** What the agent wants to do, as one short sentence starting with a verb. */
export function approvalSentence(view: View): string {
  const slug = view.action?.toolSlug?.toUpperCase() ?? "";
  const app = appOf(view.title);
  if (view.title.startsWith("Lecture confidentielle")) return `Ouvrir un contenu confidentiel${app ? ` dans ${app}` : ""}`;
  // computer_lanes (approvals.ts renders one numbered line per lane under « Tâches en parallèle »).
  const lanes = line(view, "Tâches en parallèle");
  if (lanes) {
    const n = lanes.split("\n").filter((l) => /^\d+\. /.test(l)).length;
    if (n > 1) return `Lancer ${n} tâches en même temps, dont une sur ton ordinateur`;
  }
  if (view.kind === "app_action" && slug) {
    const to = who(line(view, "Destinataire", "Invités", "Nouveaux invités"));
    const subject = line(view, "Objet");
    const channel = line(view, "Canal");
    const amount = line(view, "Montant");
    const currency = line(view, "Devise");
    const mail = /GMAIL|OUTLOOK|ICLOUDMAIL|MAIL/.test(slug);
    if (/FORWARD/.test(slug)) return to ? `Transférer ce mail à ${to}` : "Transférer ce mail";
    if (/REPLY/.test(slug)) return to ? `Répondre à ${to}` : subject ? `Répondre à « ${short(subject, 50)} »` : "Répondre à ce message";
    if (/SEND/.test(slug)) {
      if (mail) return to ? `Envoyer ce mail à ${to}` : "Envoyer ce mail";
      if (channel) return `Envoyer ce message${app ? ` sur ${app}` : ""} (${short(channel, 40)})`;
      return to ? `Envoyer ce message à ${to}` : `Envoyer ce message${app ? ` sur ${app}` : ""}`;
    }
    if (/PAY|PAYMENT|CHARGE|TRANSFER|PURCHASE|BUY|ORDER|CHECKOUT/.test(slug)) {
      const sum = amount ? money(amount, currency) : "";
      return `Payer${sum ? ` ${sum}` : ""}${to ? ` à ${to}` : ""}`;
    }
    if (/CREATE_EVENT|QUICK_ADD|INSERT_EVENT/.test(slug)) {
      if (to) return `Inviter ${to}${subject ? ` à « ${short(subject, 50)} »` : ""}`;
      return subject ? `Créer l'événement « ${short(subject, 50)} »` : "Créer un événement";
    }
    if (/UPDATE_EVENT|PATCH_EVENT|MOVE_EVENT/.test(slug)) return subject ? `Modifier « ${short(subject, 50)} »` : "Modifier un événement";
    if (/DELETE|TRASH|REMOVE/.test(slug)) return subject ? `Supprimer « ${short(subject, 50)} »` : `Supprimer un élément${app ? ` dans ${app}` : ""}`;
    if (/PERMISSION|SHARE/.test(slug)) return to ? `Partager un fichier avec ${to}` : "Partager un fichier";
    if (/FILTER/.test(slug)) return `Créer un filtre${app ? ` dans ${app}` : ""}`;
    if (/POST|PUBLISH|TWEET/.test(slug)) return `Publier${app ? ` sur ${app}` : ""}`;
    if (/INVITE/.test(slug)) return to ? `Inviter ${to}` : `Inviter quelqu'un${app ? ` sur ${app}` : ""}`;
    if (/BOOK/.test(slug)) return subject ? `Réserver « ${short(subject, 50)} »` : "Réserver";
  }
  // "Gmail · envoyer" (an app action the patterns above do not know) → "Envoyer dans Gmail".
  const technical = /^(.+?) · (.+)$/u.exec(view.title);
  if (technical) {
    const verb = technical[2]!.split(" / ")[0]!;
    return `${verb.charAt(0).toUpperCase()}${verb.slice(1)} dans ${technical[1]}`;
  }
  return view.title.trim() || "Une action à valider";
}

export type ApprovalPreview = { kind: "quote"; text: string } | { kind: "amount"; text: string } | { kind: "warning"; text: string };

/** The one thing worth reading before deciding: a warning first, then the words sent, then an amount. */
export function approvalPreview(view: View): ApprovalPreview | null {
  const warning = line(view, "Attention");
  if (warning) return { kind: "warning", text: warning };
  const quoted = view.lines.find((l) => QUOTED_LABELS.has(l.label) && l.value.trim());
  if (quoted) return { kind: "quote", text: quoted.value.trim() };
  const amount = line(view, "Montant");
  if (amount) {
    const currency = line(view, "Devise");
    return { kind: "amount", text: money(amount, currency) };
  }
  return null;
}

/** The first line of a preview inset in the conversation (V2 §6.3): who it goes to and where (« À lea@… · Gmail »). */
export function approvalInsetLabel(view: View): string | null {
  const app = appOf(view.title);
  const to = who(line(view, "Destinataire", "Invités", "Nouveaux invités"));
  if (to) return app ? `À ${to} · ${app}` : `À ${to}`;
  return app;
}

/** One word once decided. */
export function approvalOutcome(view: View & { error?: string }): string {
  const slug = view.action?.toolSlug?.toUpperCase() ?? "";
  switch (view.status) {
    case "executed":
    case "approved":
      if (/SEND|REPLY|FORWARD/.test(slug)) return "Envoyé.";
      if (/PAY|PAYMENT|CHARGE|TRANSFER|PURCHASE|BUY|ORDER|CHECKOUT/.test(slug)) return "Payé.";
      return "C'est fait.";
    case "rejected":
      return "Refusé.";
    case "expired":
      return "Expiré.";
    case "failed":
      return "Échec.";
    default:
      return "";
  }
}

/** « Toujours oui pour … »: the recipient when the rule names one, else its short French wording. */
export function alwaysShort(always: string): string {
  const m = / à ([^ ]+@[^ ]+|\+?\d[\d ]{5,}|[a-z0-9.-]+\.[a-z]{2,})(?: |$)/iu.exec(always);
  return m ? m[1]! : always;
}
